import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import {
  useGetPipeline,
  useGetSelectedApplication,
  useGetInterviews,
} from '../hooks/backend/interviews'
import { useInterviewDecisions } from '../hooks/useInterviewDecisions'
import { useGetDriverManagerBoard, useGetManagerOptions } from '../hooks/backend/drivers'
import { useManager } from '../hooks/useManager'
import { useAccess } from '../hooks/useAccess'
import { useCapabilities } from '../hooks/useCapabilities'
import { cn } from '../lib/shadcn/utils'
import { PipelineList } from '../components/interviews/PipelineList'
import { ApplicationDetail } from '../components/interviews/ApplicationDetail'
import type { OutcomeAction } from '../components/interviews/DecisionActions'
import type {
  ApplicationRow,
  SelectedApplication,
  InterviewRow,
} from '../utils/interviews'
import type { DriverBoardRow, ManagerOption } from '../utils/managers'

type SubFilter = 'all' | 'active' | 'offboarding' | 'compliance' | 'suspended' | 'dismissed' | 'resigned'

const SUBFILTERS: { key: SubFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  // An offboarding case is open: out of Active until the exit is confirmed (1711).
  { key: 'offboarding', label: 'Offboarding' },
  { key: 'compliance', label: 'Compliance' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'dismissed', label: 'Dismissed' },
  { key: 'resigned', label: 'Resigned' },
]

const ALL_STATUSES = ['active', 'offboarding', 'compliance', 'suspended', 'dismissed', 'resigned']

function statusesFor(filter: SubFilter): string[] {
  return filter === 'all' ? ALL_STATUSES : [filter]
}

export default function ActiveDrivers() {
  const { linked, loading: managerLoading } = useManager()
  const { canAct, isViewer } = useAccess()
  const { canEditDrivers } = useCapabilities()
  const [search, setSearch] = useState('')
  const [subFilter, setSubFilter] = useState<SubFilter>('all')
  // The search box in the top bar opens a driver here with ?app=.
  const [params] = useSearchParams()
  const appParam = params.get('app')
  const [selectedId, setSelectedId] = useState<string | null>(appParam)
  useEffect(() => {
    if (appParam) setSelectedId(appParam)
  }, [appParam])
  // 'all', a manager's person_id, or 'attention' for anyone unassigned or whose
  // manager disagrees with the manager of the car they are on.
  const [managerFilter, setManagerFilter] = useState<string>('all')

  const managerBoard = useGetDriverManagerBoard()
  const managerOptions = useGetManagerOptions()
  const boardTrigger = managerBoard.trigger
  const managerOptionsTrigger = managerOptions.trigger

  const pipeline = useGetPipeline()
  const selApp = useGetSelectedApplication()
  const interviews = useGetInterviews()
  const { submit, busyId } = useInterviewDecisions()

  const pipelineTrigger = pipeline.trigger
  const selTrigger = selApp.trigger
  const ivTrigger = interviews.trigger

  const loadPipeline = useCallback(() => {
    pipelineTrigger({ statuses: statusesFor(subFilter) }, { skipCache: true })
    // 'all' rather than 'active': this list also shows compliance, suspended,
    // dismissed and resigned drivers, and a blank manager on one of those would read as a
    // missing assignment rather than a filtered-out row.
    boardTrigger({ status: 'all' }, { skipCache: true })
    void managerOptionsTrigger({})
  }, [pipelineTrigger, subFilter, boardTrigger, managerOptionsTrigger])

  useEffect(() => {
    loadPipeline()
  }, [loadPipeline])

  const loadSelected = useCallback(
    (id: string) => {
      selTrigger({ applicationId: id }, { skipCache: true })
      ivTrigger({ applicationId: id }, { skipCache: true })
    },
    [selTrigger, ivTrigger],
  )

  useEffect(() => {
    if (selectedId) loadSelected(selectedId)
  }, [selectedId, loadSelected])

  const allApplications = (pipeline.data as ApplicationRow[] | undefined) ?? []

  // Manager per application, so the list can be narrowed to one manager's book.
  const boardByApplication = useMemo(() => {
    const rows = (managerBoard.data as DriverBoardRow[] | undefined) ?? []
    return new Map(
      rows.filter((r) => r.application_id).map((r) => [r.application_id as string, r]),
    )
  }, [managerBoard.data])

  const managerList = (managerOptions.data as ManagerOption[] | undefined) ?? []

  const attentionCount = useMemo(() => {
    let n = 0
    for (const a of allApplications) {
      const state = boardByApplication.get(a.application_id)?.manager_state
      if (state === 'mismatch' || state === 'unassigned' || state === 'car_unassigned') n++
    }
    return n
  }, [allApplications, boardByApplication])

  // Text search over name / email / phone, then the manager filter. Both applied
  // client-side, same as the existing search.
  const applications = useMemo(() => {
    const term = search.trim().toLowerCase()
    return allApplications.filter((a) => {
      if (term) {
        const hay = [a.full_name, a.primary_email, a.primary_phone]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        if (!hay.includes(term)) return false
      }
      if (managerFilter === 'all') return true
      const row = boardByApplication.get(a.application_id)
      if (managerFilter === 'attention') {
        const state = row?.manager_state
        return state === 'mismatch' || state === 'unassigned' || state === 'car_unassigned'
      }
      return row?.manager_person_id === managerFilter
    })
  }, [allApplications, search, managerFilter, boardByApplication])

  const selectedApplication = (selApp.data as SelectedApplication | null | undefined) ?? null
  const interviewRows = (interviews.data as InterviewRow[] | undefined) ?? []

  // Viewers are read-only; managers/admins can act once linked to a person.
  const detailCanAct = canAct && linked

  const handleSubmit = useCallback(
    async (interviewId: string, action: OutcomeAction, notes: string | null) => {
      await submit(interviewId, action, notes, () => {
        if (selectedId) loadSelected(selectedId)
        loadPipeline()
      })
    },
    [submit, selectedId, loadSelected, loadPipeline],
  )

  return (
    <div className="flex h-full min-h-0 flex-col lg:grid lg:grid-cols-[380px_1fr]">
      {/* List — hidden on mobile once a driver is selected. */}
      <aside
        className={cn(
          'min-h-0 flex-1 flex-col border-b border-border bg-card lg:flex lg:flex-none lg:border-b-0 lg:border-r',
          selectedId ? 'hidden lg:flex' : 'flex',
        )}
      >
        <div className="border-b border-border px-3 py-2 text-xs text-muted-foreground lg:px-4">
          Active drivers
        </div>

        {/* Status sub-filter. */}
        <div className="border-b border-border p-3">
          <div className="flex flex-wrap gap-1 rounded-md bg-muted p-1">
            {SUBFILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setSubFilter(f.key)}
                className={cn(
                  'flex-1 rounded-sm px-2 py-1.5 text-xs font-medium transition-colors',
                  subFilter === f.key
                    ? 'bg-background text-foreground shadow-retool-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Manager filter. Lets a Driver Servant see only their own book, and
            surfaces anyone whose manager needs a decision. The attention chip is
            hidden when there is nothing to attend to, so its presence means
            something. */}
        <div className="border-b border-border px-3 pb-3">
          <div className="flex flex-wrap gap-1">
            {[
              { key: 'all', label: 'All managers' },
              ...managerList.map((m) => ({
                key: m.manager_person_id,
                label: m.manager_name,
              })),
              ...(attentionCount > 0
                ? [{ key: 'attention', label: `Needs attention (${attentionCount})` }]
                : []),
            ].map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setManagerFilter(f.key)}
                className={cn(
                  'rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
                  managerFilter === f.key
                    ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                    : 'border-border text-muted-foreground hover:text-foreground',
                  f.key === 'attention' && managerFilter !== f.key && 'border-amber-400 text-amber-600',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <PipelineList
          applications={applications}
          loading={pipeline.loading}
          error={pipeline.error}
          search={search}
          onSearchChange={setSearch}
          selectedStatuses={statusesFor(subFilter)}
          onStatusChange={() => {}}
          selectedId={selectedId}
          onSelect={setSelectedId}
          showStatusFilter={false}
        />
      </aside>

      {/* Detail — hidden on mobile until a driver is selected. */}
      <main
        className={cn(
          'min-h-0 flex-1 flex-col bg-background lg:flex',
          selectedId ? 'flex' : 'hidden lg:flex',
        )}
      >
        {selectedId && (
          <button
            type="button"
            onClick={() => setSelectedId(null)}
            className="flex items-center gap-2 border-b border-border px-4 py-3 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground lg:hidden"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to drivers
          </button>
        )}
        <div className="min-h-0 flex-1">
          <ApplicationDetail
            app={selectedApplication}
            appLoading={selApp.loading}
            appError={selApp.error}
            interviews={interviewRows}
            interviewsLoading={interviews.loading}
            interviewsError={interviews.error}
            canAct={detailCanAct}
            isViewer={isViewer}
            managerLinked={linked}
            managerLoading={managerLoading}
            busyInterviewId={busyId}
            onSubmit={handleSubmit}
            canEditDrivers={canEditDrivers}
            isActiveTab={true}
            boardDriverId={selectedId ? boardByApplication.get(selectedId)?.driver_id ?? null : null}
            onDriverSaved={() => {
              if (selectedId) loadSelected(selectedId)
              loadPipeline()
            }}
          />
        </div>
      </main>
    </div>
  )
}
